import _ from "lodash";
import { useCallback, useRef, useState } from "react";
import Geocode from "react-geocode";
import TagManager from "react-gtm-module";
import {
  doSearch as doSearchService,
  doSearchStream as doStreamService,
} from "../Services/Data.service";
import { computeSearchNavigation, markSearchedOnce } from "./SearchUtils";
import { DEFAULT_MAP_ZOOM, ShowMessage } from "./Utils";

export const useSearchLogic = ({
  userContext,
  mapContext,
  filters,
  isStrictProductSearch,
  addLayer,
  fetchDelivery,
  setResults,
  setShowSearch,
  setIsSuggestingStore,
  params,
  initialCoordinates,
}) => {
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({
    coordinates: params.get("coordinates")
      ? params
          .get("coordinates")
          .split(",")
          .map((c) => (!isNaN(c) ? Number(c) : undefined))
          .filter((c) => !!c).length >= 2
        ? params
            .get("coordinates")
            .split(",")
            .slice(0, 2)
            .map((c) => Number(c))
        : undefined
      : initialCoordinates,
    zoom: params.get("zoom")
      ? Number(params.get("zoom"))
      : userContext.state?.config?.initialZoom || DEFAULT_MAP_ZOOM,
    _searchParams: {
      preventFly: false,
      zipcode: params.get("zipcode"),
    },
  });

  const abortControllerRef = useRef(null);
  const searchId = useRef(null);
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const search = useCallback(
    async (shouldFlyToCoordinates, zoom, _searchParams) => {
      const newSearchId = new Date().getTime();
      searchId.current = newSearchId;

      const filterResults = (oldResults) =>
        _.reduce(
          _.toPairs(filters),
          (acc, [key, value]) => {
            if (!value) {
              return acc;
            }
            return _.filter(acc, (res) => {
              if (key === "product") {
                const productIdsToFilter = value || [];
                const resultProductIds = (res.products || []).map(
                  (p) => p.product_id,
                );

                if (
                  productIdsToFilter.length === 0 ||
                  isStrictProductSearch.current
                ) {
                  return productIdsToFilter.every((id) =>
                    resultProductIds.includes(id),
                  );
                } else {
                  return productIdsToFilter.some((id) =>
                    resultProductIds.includes(id),
                  );
                }
              }

              if (key === "chain" && value === "null") {
                if (userContext.state?.config?.limitChainsFilter) {
                  return (
                    !res.chain ||
                    !userContext.state.config.limitChainsFilter.includes(
                      res.chain,
                    )
                  );
                }
                return !res.chain;
              }

              if (key === "type") {
                if (!value) {
                  return true;
                }
                if (value.includes("store") && !res.type) {
                  return true;
                }
                return (
                  res[key] && _.toLower(value).includes(_.toLower(res[key]))
                );
              }

              if (key === "country" && (value === "CA" || value === "Canada")) {
                const canadaProvinces = [
                  "AB",
                  "BC",
                  "MB",
                  "NB",
                  "NL",
                  "NS",
                  "NT",
                  "NU",
                  "ON",
                  "PE",
                  "QC",
                  "SK",
                  "YT",
                ];
                return (
                  res.state &&
                  canadaProvinces.includes(_.toUpper(res.state).trim())
                );
              }

              if (key === "country" && (value === "US" || value === "USA")) {
                const usStates = [
                  "AL",
                  "AK",
                  "AZ",
                  "AR",
                  "CA",
                  "CO",
                  "CT",
                  "DE",
                  "FL",
                  "GA",
                  "HI",
                  "ID",
                  "IL",
                  "IN",
                  "IA",
                  "KS",
                  "KY",
                  "LA",
                  "ME",
                  "MD",
                  "MA",
                  "MI",
                  "MN",
                  "MS",
                  "MO",
                  "MT",
                  "NE",
                  "NV",
                  "NH",
                  "NJ",
                  "NM",
                  "NY",
                  "NC",
                  "ND",
                  "OH",
                  "OK",
                  "OR",
                  "PA",
                  "RI",
                  "SC",
                  "SD",
                  "TN",
                  "TX",
                  "UT",
                  "VT",
                  "VA",
                  "WA",
                  "WV",
                  "WI",
                  "WY",
                  "DC",
                  "PR",
                ];
                return (
                  res.state && usStates.includes(_.toUpper(res.state).trim())
                );
              }

              return res[key] && _.toLower(value).includes(_.toLower(res[key]));
            });
          },
          oldResults,
        );

      setLoading(true);
      setResults((oldResults) => {
        const filtered = filterResults(oldResults);
        addLayer(filtered);
        return filtered;
      });

      if (
        !Number.isFinite(shouldFlyToCoordinates?.lat) ||
        !Number.isFinite(shouldFlyToCoordinates?.lng)
      ) {
        ShowMessage("Please enter a valid address or zipcode");
        setLoading(false);
        return;
      }

      const zip = _searchParams?.zipcode;
      const performSearch = async (isFallback = false) => {
        return await (userContext.state?.config?.useStoreStream
          ? doStreamService
          : doSearchService)(
          [shouldFlyToCoordinates.lat, shouldFlyToCoordinates.lng],
          zip,
          zoom || DEFAULT_MAP_ZOOM,
          userContext.state?.config?.limitChainsFilter
            ? filters.chain === "null"
              ? "*"
              : filters.chain
            : filters.chain,
          filters.product?.length
            ? typeof filters.product === "string"
              ? [filters.product]
              : filters.product
            : [],
          undefined,
          isFallback,
          abortControllerRef.current,
        );
      };

      const parseResponse = async (response) => {
        if (!userContext.state?.config?.useStoreStream) {
          return response;
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let result = "";
        let results = [];
        const throttleAddLayer = _.throttle(addLayer, 100);
        let parsedFirstItem = false;

        try {
          let readResult;
          while (!(readResult = await reader.read()).done) {
            result += decoder.decode(readResult.value, { stream: true });
            const lines = result.split("\n");
            result = lines.pop() || "";

            for (const line of lines) {
              results.push(JSON.parse(line));
              if (newSearchId < searchId.current) {
                return;
              }

              const nav = computeSearchNavigation({
                results,
                searchParams: searchParamsRef.current,
                config: userContext.state?.config,
                urlParams: params,
                initialCoordinates: shouldFlyToCoordinates,
              });

              throttleAddLayer(
                results,
                parsedFirstItem || nav.preventFly
                  ? false
                  : nav.flyToCoordinates,
                _searchParams?.geopoliticalRegion,
              );
              setResults(results);
              if (!parsedFirstItem) {
                parsedFirstItem = true;
              }
            }
          }
        } catch (error) {
          console.log(error);
        }
        return results;
      };

      try {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;

        let response = await performSearch(false);
        let results = await parseResponse(response);

        let nav = computeSearchNavigation({
          results,
          searchParams: searchParamsRef.current,
          config: userContext.state?.config,
          urlParams: params,
          initialCoordinates: shouldFlyToCoordinates,
        });

        if (nav.fallbackRequired) {
          const fallbackController = new AbortController();
          abortControllerRef.current = fallbackController;
          response = await performSearch(true);
          results = await parseResponse(response);
          nav = computeSearchNavigation({
            results,
            searchParams: searchParamsRef.current,
            config: userContext.state?.config,
            urlParams: params,
            initialCoordinates: shouldFlyToCoordinates,
          });
        }

        if (newSearchId < searchId.current) {
          return;
        }

        fetchDelivery([nav.flyToCoordinates.lat, nav.flyToCoordinates.lng]);
        const filteredResults = filterResults(results);

        TagManager.dataLayer({
          dataLayer: {
            event: "searchChange",
            category: "Search",
            action: _searchParams?.term,
            snowplowSearchFilters: Object.entries({
              ...filters,
              coordinates: nav.flyToCoordinates,
              zip,
              zoom,
            }).reduce(
              (acc, [key, value]) => ({ ...acc, [key]: JSON.stringify(value) }),
              {},
            ),
            snowplowSearchTotal: results?.length || 0,
            snowplowSearchPage: filteredResults?.length || 0,
          },
        });

        const isFirstSearch = _searchParams?.searchedFirstTime === undefined;

        addLayer(
          filteredResults,
          nav.preventFly || isFirstSearch ? false : nav.flyToCoordinates,
          _searchParams?.geopoliticalRegion,
        );
        setResults(filteredResults);
        setLoading(false);
        setShowSearch?.(false);
        setIsSuggestingStore?.(false);
        return { results, filteredResults, nav };
      } catch (error) {
        if (error.name !== "AbortError") {
          ShowMessage(undefined, {
            message: "" + error.message,
            filename: "useSearchLogic:search",
            error,
          });
        }
        setLoading(false);
      }
    },
    [
      addLayer,
      filters,
      userContext.state,
      fetchDelivery,
      setResults,
      setShowSearch,
      setIsSuggestingStore,
      params,
      isStrictProductSearch.current,
    ],
  );

  const searchWithTerm = useCallback(
    async (termArg, currentTerm) => {
      const _term = termArg || currentTerm;
      if (_term === "") {
        ShowMessage("Please enter a valid address or zipcode");
        return;
      }
      setLoading(true);

      let data;
      try {
        data = await Geocode.fromAddress(_term);
      } catch (error) {
        console.log("Geocode no results");
      }

      setLoading(false);
      if (data?.results?.[0]) {
        const { geometry } = data?.results[0];
        let geopoliticalRegion = "street_address";
        for (const type of [
          "street_address",
          "postal_code",
          "locality",
          "administrative_area_level_1",
          "country",
        ]) {
          if (data?.results[0].types.includes(type)) {
            geopoliticalRegion = type;
            break;
          }
        }
        let zoom = undefined;
        switch (geopoliticalRegion) {
          case "street_address":
          case "postal_code":
            zoom = DEFAULT_MAP_ZOOM + 4;
            break;
          case "locality":
            zoom = DEFAULT_MAP_ZOOM + 2;
            break;
          case "administrative_area_level_1":
            zoom = DEFAULT_MAP_ZOOM - 3;
            break;
          case "country":
            zoom = DEFAULT_MAP_ZOOM - 6;
            break;
          default:
            break;
        }
        setSearchParams((oldParams) => ({
          coordinates: [geometry.location.lat, geometry.location.lng],
          zoom: zoom || oldParams.zoom,
          _searchParams: {
            ...markSearchedOnce(oldParams),
            zipcode: _term,
            geopoliticalRegion,
            term: _term,
            preventFly: false,
          },
        }));
      } else {
        setLoading(true);
        doSearchService(
          undefined,
          undefined,
          undefined,
          _term,
          undefined,
          undefined,
          false,
          undefined,
          1,
        )
          .then((stores) => {
            if (stores?.[0]?.address) {
              searchWithTerm(stores[0].address);
            }
          })
          .finally(() => {
            setLoading(false);
          });
      }
    },
    [searchParams.zoom],
  );

  return {
    loading,
    setLoading,
    searchParams,
    setSearchParams,
    search,
    searchWithTerm,
    abortControllerRef,
    searchId,
  };
};
